import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import { getMediaErrorType, getUploadErrorType, isScreenShareDenial } from './recorderErrors';

export type RecordingEndReason = 'limitMinutesReached' | 'uploadFailed' | 'userStopped';
export type RecordingCancelReason = 'deleteClicked' | 'tabClosed';
type RecordingPermissionType = 'camera' | 'microphone' | 'screenShare';

/** The devices a started recording actually got. */
export type RecordingAnalytics = { hasCamera: boolean; hasMicrophone: boolean };

/** Which part of the start was running when it failed: it decides how the failure is reported. */
export type RecordingStartStep = 'media' | 'screenShare' | 'upload';

/** The server recording the event belongs to, from the moment it is created; nothing before that. */
type RecordingIdRef = null | string | undefined;

const withRecordingId = (recordingId: RecordingIdRef) => (recordingId ? { recordingId } : {});

export const trackPermission = (type: RecordingPermissionType, isGranted: boolean) =>
  isGranted ? MixpanelEvents.recordingPermissionGranted({ type }) : MixpanelEvents.recordingPermissionDenied({ type });

export const trackUploadFailure = (error: unknown, recordingId?: RecordingIdRef) =>
  MixpanelEvents.recordingUploadFailed({ errorType: getUploadErrorType(error), ...withRecordingId(recordingId) });

export const trackStartFailure = (step: RecordingStartStep, error: unknown, recordingId?: RecordingIdRef) => {
  if (step === 'screenShare' && isScreenShareDenial(error)) trackPermission('screenShare', false);
  else if (step === 'upload') trackUploadFailure(error, recordingId);
  else {
    MixpanelEvents.recordingFailed({
      durationSec: 0,
      errorType: getMediaErrorType(error),
      ...withRecordingId(recordingId),
    });
  }
};

export const trackStarted = ({ hasCamera, hasMicrophone }: RecordingAnalytics, recordingId: RecordingIdRef) =>
  MixpanelEvents.recordingStarted({ hasCamera, hasMicrophone, ...withRecordingId(recordingId) });

export const trackCompleted = (
  { hasCamera, hasMicrophone }: RecordingAnalytics,
  endReason: RecordingEndReason,
  durationSec: number,
  recordingId?: RecordingIdRef,
) =>
  MixpanelEvents.recordingCompleted({
    cameraUsed: hasCamera,
    durationSec,
    endReason,
    micUsed: hasMicrophone,
    ...withRecordingId(recordingId),
  });

export const trackCancelled = (cancelReason: RecordingCancelReason, elapsedMs: number, recordingId?: RecordingIdRef) =>
  MixpanelEvents.recordingCancelled({
    durationSec: Math.floor(elapsedMs / 1000),
    cancelReason,
    ...withRecordingId(recordingId),
  });

/** The recording is uploaded and finished on the server (the save that hands it over to processing). */
export const trackSaved = (recordingId: string) => MixpanelEvents.recordingSaved({ recordingId });
